import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { ArrowRight, Cpu, Layers, GitFork, Zap, Shield, Code2 } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen relative">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="landing-hero">
        <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none"></div>
        <div className="max-w-[1400px] mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-12 gap-10 relative">
          <div className="lg:col-span-7 animate-fade-in-up">
            <div className="chip chip-accent mb-6" data-testid="hero-eyebrow">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--accent)] animate-pulse"></span>
              LIVE · MetaTrader 4 & 5 · Broker-connected
            </div>
            <h1 className="font-display font-light text-5xl md:text-7xl leading-[1.02] tracking-tight text-white">
              Forge <span className="italic font-normal text-[var(--accent)]">MT4/MT5</span>
              <br/> trading robots.
              <br/> <span className="text-[var(--text-secondary)]">Wire them to your broker.</span>
            </h1>
            <p className="mt-8 max-w-xl text-[var(--text-secondary)] text-lg leading-relaxed">
              A visual strategy studio, an AI code generator, and a clone-anything reverse
              engineer for MQL4 Expert Advisors. Ship production-ready <span className="font-mono text-white">.mq4</span> files
              your MetaEditor compiles cleanly.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/signup" data-testid="hero-cta-primary" className="btn-primary flex items-center gap-2 text-base">
                Start building <ArrowRight size={16} />
              </Link>
              <Link to="/login" data-testid="hero-cta-secondary" className="btn-outline text-base">
                I already have an account
              </Link>
            </div>
            <div className="mt-14 grid grid-cols-3 gap-6 max-w-md">
              {[
                {n:"11+", l:"indicators"},
                {n:"3",   l:"AI models"},
                {n:"0ms", l:"vendor lock-in"},
              ].map((s,i) => (
                <div key={i} className="border-l border-white/10 pl-4">
                  <div className="font-display text-3xl text-white">{s.n}</div>
                  <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)] mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: code preview mock */}
          <div className="lg:col-span-5 relative animate-fade-in-up" style={{animationDelay:"120ms"}}>
            <div className="card-sharp relative overflow-hidden scanlines">
              <div className="flex items-center gap-2 px-3 py-2.5 border-b border-white/5 bg-black/40">
                <span className="h-2.5 w-2.5 rounded-full bg-[#FF3366]/70"></span>
                <span className="h-2.5 w-2.5 rounded-full bg-[#FFB800]/70"></span>
                <span className="h-2.5 w-2.5 rounded-full bg-[#00FF66]/70"></span>
                <span className="ml-3 text-[11px] font-mono text-[var(--text-secondary)]">RSI_Reversal.mq4</span>
              </div>
              <pre className="p-5 text-[12.5px] font-mono leading-relaxed text-[#D4D4D4]">
{`#property strict

input double InpLotSize        = 0.10;
input int    InpStopLossPips   = 30;
input int    InpTakeProfitPips = 60;

void OnTick() {
   if(iRSI(_Symbol, PERIOD_H1, 14, PRICE_CLOSE, 0) < 30)
      OrderSend(_Symbol, OP_BUY, InpLotSize, Ask,
                3, Bid - InpStopLossPips * _Point,
                Bid + InpTakeProfitPips * _Point,
                "RSI_Reversal", 20260101, 0, clrLimeGreen);
}`}
              </pre>
            </div>
            <div className="absolute -bottom-4 -right-4 chip chip-profit">
              <span>◆</span> BUILDS CLEAN
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section className="max-w-[1400px] mx-auto px-6 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <FeatureCard testId="feat-visual" span="md:col-span-4" icon={<Layers/>} title="Visual Strategy Builder"
            body="Drag in indicators — RSI, MA, MACD, Bollinger, Ichimoku, Stochastic, ATR, ADX, CCI. Compose entry & exit conditions, wire up risk. See MQL4 update live."/>
          <FeatureCard testId="feat-ai" span="md:col-span-2" icon={<Cpu/>} title="AI Generator"
            body="Describe your idea. Claude Sonnet 4.6, GPT-5.2, or Gemini 3 Flash write the .mq4."/>
          <FeatureCard testId="feat-clone" span="md:col-span-2" icon={<GitFork/>} title="Clone Existing EAs"
            body="Upload an .mq4 file. AI extracts its logic and drops it into the builder to remix."/>
          <FeatureCard testId="feat-risk" span="md:col-span-2" icon={<Shield/>} title="Risk Management"
            body="Stop loss, take profit, trailing stop, spread guard, magic-number isolation."/>
          <FeatureCard testId="feat-code" span="md:col-span-2" icon={<Code2/>} title="Ships as .mq4"
            body="Every strategy exports as a compile-ready file with IDE-grade syntax colors."/>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 pb-32">
        <div className="card-sharp p-10 md:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <div className="chip mb-4"><Zap size={12}/> READY WHEN YOU ARE</div>
            <h2 className="font-display text-3xl md:text-4xl text-white tracking-tight">
              Trade the strategy in your head. Not the one everyone else is running.
            </h2>
          </div>
          <Link to="/signup" data-testid="cta-bottom" className="btn-primary flex items-center gap-2 whitespace-nowrap">
            Create your first robot <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/5 py-6">
        <div className="max-w-[1400px] mx-auto px-6 text-xs font-mono text-[var(--text-muted)] flex justify-between">
          <span>© 2026 MQL4Forge</span>
          <span>Not affiliated with MetaQuotes Software.</span>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, body, span, testId }) {
  return (
    <div data-testid={testId} className={`card-sharp p-6 ${span} min-h-[190px]`}>
      <div className="h-9 w-9 flex items-center justify-center rounded-sm border border-white/10 text-[var(--accent)]">
        {React.cloneElement(icon, { size: 16 })}
      </div>
      <h3 className="font-display text-xl text-white mt-4">{title}</h3>
      <p className="text-sm text-[var(--text-secondary)] leading-relaxed mt-2">{body}</p>
    </div>
  );
}
